/**
 * useInfoPopover — the open / pin / keyboard / placement behaviour behind InfoPopover,
 * for the few popovers whose panel is too rich for InfoPopover's heading + body
 * (CallsCell, NotesCell). Everything else should use InfoPopover itself.
 *
 * Split out in docs/DUPLICATION_AUDIT.md B31, which moved 24 hand-rolled hover-only
 * tips onto the shared behaviour. Hover-only meant a tablet could never open them.
 *
 * Two modes:
 *   - 'pin' (default): the trigger IS the control. It is focusable (role=button),
 *     opens on hover or focus, and a click, Enter or Space pins it open. A pinned
 *     popover closes on a click outside it, Escape, or a scroll.
 *   - 'describe': the trigger wraps a control that already has its own click (the
 *     Mobile / Station mode buttons, the notes badge). It adds no role or tab stop;
 *     hovering or focusing the control inside opens it, and clicking the control
 *     closes it so the popover never sits over whatever the click opened.
 * In both modes Escape and a scroll close an open popover.
 *
 * Only one popover is open at a time, across every instance of either mode.
 *
 * @param {object}  [opts]
 * @param {number}  [opts.maxWidth=300]  — px; also decides when the panel pins to the
 *        viewport's right edge instead of the trigger's left
 * @param {number}  [opts.flipBelow=120] — open upward when less than this much room
 *        is left below the trigger (and more is above). Taller panels pass more.
 * @param {boolean} [opts.enabled=true]  — false keeps it closed (nothing to show)
 * @param {'pin'|'describe'} [opts.mode='pin']
 * @returns {{ open: boolean, style: object|null, panelId: string, triggerProps: object, close: () => void }}
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

/* Close callbacks of every currently-open instance. A registry rather than
   React context because these are leaves scattered through unrelated tables and
   a provider would have to wrap all of them to achieve the same thing. */
const openInstances = new Set();

function closeOthers(keep) {
    openInstances.forEach((close) => {
        if (close !== keep) close();
    });
}

export default function useInfoPopover({ maxWidth = 300, flipBelow = 120, enabled = true, mode = 'pin' } = {}) {
    const [hovering, setHovering] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [style, setStyle] = useState(null);
    const ref = useRef(null);
    const panelId = useId();

    const open = Boolean(enabled) && (hovering || pinned);

    const close = useCallback(() => {
        setHovering(false);
        setPinned(false);
    }, []);

    /* Measured at open time, not on every scroll or resize. The pane this app is
       verified in never delivers resize or ResizeObserver callbacks, so anything
       depending on them would work in a real browser and silently not here —
       and a popover is short-lived enough that one measurement is honest. */
    const place = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const spaceRight = window.innerWidth - r.left;
        const flipUp = spaceBelow < flipBelow && r.top > spaceBelow;
        const pinRight = spaceRight < maxWidth + 24;
        setStyle({
            position: 'fixed',
            zIndex: 9999,
            maxWidth: `${maxWidth}px`,
            left: pinRight ? undefined : `${Math.max(8, r.left)}px`,
            right: pinRight ? '16px' : undefined,
            top: flipUp ? undefined : `${r.bottom + 6}px`,
            bottom: flipUp ? `${window.innerHeight - r.top + 6}px` : undefined,
        });
    }, [maxWidth, flipBelow]);

    const show = useCallback(() => {
        if (!enabled) return;
        place();
        closeOthers(close);
        setHovering(true);
    }, [enabled, place, close]);

    const togglePin = useCallback(() => {
        if (!enabled) return;
        place();
        closeOthers(close);
        setPinned((v) => !v);
        setHovering(false);
    }, [enabled, place, close]);

    // Register while open so a sibling opening can close this one.
    useEffect(() => {
        if (!open) return undefined;
        openInstances.add(close);
        return () => openInstances.delete(close);
    }, [open, close]);

    /* Escape and scroll close an open popover, hovered or pinned. Scroll because
       the panel is fixed-positioned from a one-time measurement, so it would be
       left floating away from the row it describes — a stale explanation pointing
       at the wrong row is worse than none. Capture phase, so an inner scroller
       (.sr-table-wrap) is caught too.
       The Escape that closes a popover is consumed, in the capture phase, so it does
       not also close the drawer the tip sits in: the drawers listen on document in
       the bubble phase, and several of them ignore defaultPrevented. */
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close();
        };
        document.addEventListener('keydown', onKey, true);
        window.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('scroll', close, true);
        };
    }, [open, close]);

    // A click outside dismisses a PINNED popover.
    useEffect(() => {
        if (!pinned) return undefined;
        const onDown = (e) => { if (!ref.current?.contains(e.target)) setPinned(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [pinned]);

    const hoverProps = {
        ref,
        onMouseEnter: show,
        onMouseLeave: () => setHovering(false),
        onFocus: show,
        onBlur: () => setHovering(false),
    };

    const triggerProps = mode === 'describe'
        ? { ...hoverProps, onClick: close }
        : {
            ...hoverProps,
            onClick: togglePin,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePin();
                }
            },
            role: 'button',
            tabIndex: 0,
            'aria-expanded': open,
            'aria-describedby': open ? panelId : undefined,
        };

    return { open, style, panelId, triggerProps, close };
}
